import type { MetronomeSpeedUpPreset } from "@wmf/shared/metronomePractice";

export type MetronomeAudioMode = "accent" | "normal" | "muted";

export interface MetronomeAudioSubdivision {
  divisions: number;
  positions: readonly number[];
}

export interface MetronomeAudioPattern {
  bpm: number;
  beats: number;
  subdivision: MetronomeAudioSubdivision;
  beatModes: readonly MetronomeAudioMode[];
  subdivisionModes: readonly (readonly MetronomeAudioMode[])[];
  speedUp?: MetronomeSpeedUp;
}

export type MetronomeSpeedUp = MetronomeSpeedUpPreset;

export interface MetronomeClickEvent {
  atSeconds: number;
  frequencyHz: number;
  gain: number;
}

export const METRONOME_AUDIO_SAMPLE_RATE = 12_000;
export const METRONOME_CLICK_SECONDS = 0.045;
export const METRONOME_MEDIA_MIN_SECONDS = 10 * 60;

function clickShape(mode: MetronomeAudioMode, mainBeat: boolean) {
  return {
    frequencyHz: mode === "accent" ? 1_420 : mainBeat ? 920 : 760,
    gain: mode === "accent" ? 0.19 : mainBeat ? 0.12 : 0.085,
  };
}

function sameValues<T>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

/**
 * Audio patterns are immutable snapshots. Equality lets the provider reconcile
 * settings that changed while `HTMLAudioElement.play()` was still pending
 * without rebuilding an identical media file after every ordinary start.
 */
export function metronomeAudioPatternsEqual(
  left: MetronomeAudioPattern,
  right: MetronomeAudioPattern,
): boolean {
  return (
    left.bpm === right.bpm &&
    left.speedUp?.increaseBpm === right.speedUp?.increaseBpm &&
    left.speedUp?.everyBars === right.speedUp?.everyBars &&
    left.speedUp?.targetBpm === right.speedUp?.targetBpm &&
    left.beats === right.beats &&
    left.subdivision.divisions === right.subdivision.divisions &&
    sameValues(left.subdivision.positions, right.subdivision.positions) &&
    sameValues(left.beatModes, right.beatModes) &&
    left.subdivisionModes.length === right.subdivisionModes.length &&
    left.subdivisionModes.every((row, index) =>
      sameValues(row, right.subdivisionModes[index] ?? []),
    )
  );
}

export function metronomeLoopDurationSeconds(pattern: MetronomeAudioPattern): number {
  if (!Number.isFinite(pattern.bpm) || pattern.bpm <= 0) {
    throw new Error("Metronome BPM must be greater than zero.");
  }
  if (!Number.isInteger(pattern.beats) || pattern.beats <= 0) {
    throw new Error("Metronome beat count must be a positive integer.");
  }
  return pattern.beats * (60 / pattern.bpm);
}

export function metronomeClickEvents(pattern: MetronomeAudioPattern): MetronomeClickEvent[] {
  const beatSeconds = 60 / pattern.bpm;
  const extras = pattern.subdivision.positions.filter((position) => position !== 0);
  const events: MetronomeClickEvent[] = [];

  for (let beatIndex = 0; beatIndex < pattern.beats; beatIndex += 1) {
    const beatStart = beatIndex * beatSeconds;
    const mainMode = pattern.beatModes[beatIndex] ?? "normal";
    if (mainMode !== "muted") {
      events.push({ atSeconds: beatStart, ...clickShape(mainMode, true) });
    }

    extras.forEach((position, extraIndex) => {
      const mode = pattern.subdivisionModes[beatIndex]?.[extraIndex] ?? "normal";
      if (mode === "muted") return;
      events.push({
        atSeconds:
          beatStart + beatSeconds * (position / pattern.subdivision.divisions),
        ...clickShape(mode, false),
      });
    });
  }

  return events;
}

function writeAscii(view: DataView, offset: number, value: string) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function renderBarPcmBytes(
  pattern: MetronomeAudioPattern,
  sampleRate: number,
): Uint8Array {
  const barDurationSeconds = metronomeLoopDurationSeconds(pattern);
  // Keep enough trailing silence for either rounded bar length. Long-media bar
  // starts are placed independently below so rounding can never accumulate.
  const frameCount = Math.max(1, Math.ceil(barDurationSeconds * sampleRate));
  const samples = new Float32Array(frameCount);
  const clickFrames = Math.max(1, Math.round(METRONOME_CLICK_SECONDS * sampleRate));
  const attackFrames = Math.max(1, Math.round(0.0015 * sampleRate));

  for (const event of metronomeClickEvents(pattern)) {
    const firstFrame = Math.round(event.atSeconds * sampleRate);
    const frames = Math.min(clickFrames, frameCount - firstFrame);
    for (let offset = 0; offset < frames; offset += 1) {
      const progress = offset / clickFrames;
      const attack = Math.min(1, offset / attackFrames);
      const envelope = attack * Math.exp(-6 * progress) * (1 - progress);
      const seconds = offset / sampleRate;
      const value =
        Math.sin(2 * Math.PI * event.frequencyHz * seconds) * event.gain * envelope;
      const frame = firstFrame + offset;
      samples[frame] = Math.max(-1, Math.min(1, samples[frame]! + value));
    }
  }

  const bytesPerSample = 2;
  const bytes = new Uint8Array(frameCount * bytesPerSample);
  const view = new DataView(bytes.buffer);
  for (let frame = 0; frame < frameCount; frame += 1) {
    view.setInt16(frame * bytesPerSample, Math.round(samples[frame]! * 32_767), true);
  }
  return bytes;
}

function validSpeedUp(pattern: MetronomeAudioPattern): MetronomeSpeedUp | undefined {
  const ramp = pattern.speedUp;
  return ramp && Number.isInteger(ramp.increaseBpm) && ramp.increaseBpm > 0 &&
    Number.isInteger(ramp.everyBars) && ramp.everyBars > 0 &&
    Number.isFinite(ramp.targetBpm) && ramp.targetBpm > pattern.bpm ? ramp : undefined;
}

export function metronomeBpmAtBar(pattern: MetronomeAudioPattern, barIndex: number): number {
  const ramp = validSpeedUp(pattern);
  return ramp
    ? Math.min(ramp.targetBpm, pattern.bpm + Math.floor(barIndex / ramp.everyBars) * ramp.increaseBpm)
    : pattern.bpm;
}

/** The same bar timeline drives the rendered sound and the foreground display. */
export function metronomeAudioPosition(
  pattern: MetronomeAudioPattern,
  currentTimeSeconds: number,
  firstBar = 0,
) {
  const ramp = validSpeedUp(pattern);
  let remaining = Number.isFinite(currentTimeSeconds) ? Math.max(0, currentTimeSeconds) : 0;
  let barIndex = firstBar;
  let bpm = metronomeBpmAtBar(pattern, barIndex);
  while (ramp && bpm < ramp.targetBpm) {
    const barsAtTempo = ramp.everyBars - barIndex % ramp.everyBars;
    const duration = barsAtTempo * pattern.beats * 60 / bpm;
    if (remaining < duration - 1e-9) break;
    remaining = Math.max(0, remaining - duration);
    barIndex += barsAtTempo;
    bpm = metronomeBpmAtBar(pattern, barIndex);
  }
  const beatSeconds = 60 / bpm;
  return {
    bpm,
    beatIndex: Math.floor((remaining + 0.002) / beatSeconds) % pattern.beats,
    nextBeatDelayMs: nextMetronomeBeatDelayMs(remaining, bpm),
  };
}

export function metronomeTrackLayout(
  pattern: MetronomeAudioPattern,
  minimumDurationSeconds = METRONOME_MEDIA_MIN_SECONDS,
  firstBar = 0,
) {
  metronomeLoopDurationSeconds(pattern);
  if (!Number.isFinite(minimumDurationSeconds) || minimumDurationSeconds <= 0 ||
      !Number.isSafeInteger(firstBar) || firstBar < 0) {
    throw new Error("Metronome media timeline is invalid.");
  }
  const bars: Array<{ atSeconds: number; bpm: number }> = [];
  let durationSeconds = 0;
  while (durationSeconds < minimumDurationSeconds - 1e-9) {
    const bpm = metronomeBpmAtBar(pattern, firstBar + bars.length);
    bars.push({ atSeconds: durationSeconds, bpm });
    durationSeconds += pattern.beats * 60 / bpm;
  }
  return { bars, durationSeconds, nextBar: firstBar + bars.length };
}

/**
 * Render a long PCM WAV for ordinary HTML media playback.
 *
 * PR #444 deliberately moved the audible clock to HTMLAudioElement so iOS and
 * other mobile browsers can keep the metronome alive while the page is locked.
 * A one-bar file made `HTMLAudioElement.loop` restart the media resource on
 * every bar, however, and some browsers make that restart audibly late.
 *
 * Instead, this file contains enough contiguous bars for at least ten minutes.
 * The expensive waveform is synthesised once per tempo; that rendered bar is
 * copied at each exact absolute bar boundary using native TypedArray copies.
 * Speed increases are in the audio itself, not page timers. Long ramps continue
 * in bounded tracks from firstBar, so large bar counts cannot allocate hours of
 * PCM at once or accidentally replay the opening slow bars. This keeps ordinary
 * beat-4 -> beat-1 transitions inside one file,
 * avoids millions of repeated sin/exp calculations on live edits, and avoids
 * cumulative phase drift from repeatedly rounding one bar length.
 */
export function createMetronomeLoopWavBytes(
  pattern: MetronomeAudioPattern,
  sampleRate = METRONOME_AUDIO_SAMPLE_RATE,
  minimumDurationSeconds = METRONOME_MEDIA_MIN_SECONDS,
  firstBar = 0,
): Uint8Array {
  if (!Number.isInteger(sampleRate) || sampleRate < 8_000) {
    throw new Error("Metronome sample rate is invalid.");
  }
  if (!Number.isFinite(minimumDurationSeconds) || minimumDurationSeconds <= 0) {
    throw new Error("Metronome media duration is invalid.");
  }

  const { bars, durationSeconds } = metronomeTrackLayout(pattern, minimumDurationSeconds, firstBar);
  const bytesPerSample = 2;
  const frameCount = Math.max(1, Math.round(durationSeconds * sampleRate));
  const dataBytes = frameCount * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, dataBytes, true);

  let renderedBpm = -1;
  let barPcm: Uint8Array = new Uint8Array(0);
  for (const bar of bars) {
    if (bar.bpm !== renderedBpm) {
      barPcm = renderBarPcmBytes({ ...pattern, bpm: bar.bpm }, sampleRate);
      renderedBpm = bar.bpm;
    }
    // Round absolute bar starts, never repeatedly rounded bar durations.
    const offset = 44 + Math.round(bar.atSeconds * sampleRate) * bytesPerSample;
    bytes.set(barPcm.subarray(0, Math.min(barPcm.byteLength, bytes.length - offset)), offset);
  }
  return bytes;
}

/** Milliseconds until the next main-beat boundary in the currently playing media. */
export function nextMetronomeBeatDelayMs(currentTimeSeconds: number, bpm: number): number {
  if (!Number.isFinite(currentTimeSeconds) || currentTimeSeconds < 0) return 0;
  if (!Number.isFinite(bpm) || bpm <= 0) return 0;
  const beatSeconds = 60 / bpm;
  const phase = currentTimeSeconds % beatSeconds;
  const remaining = phase < 0.002 ? beatSeconds : beatSeconds - phase;
  return Math.max(0, remaining * 1_000);
}
